import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Store, Truck, ArrowLeft, X, Shield, Star,
    MapPin, Clock, Search, Navigation,
    CheckCircle2, ShoppingCart
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Marchantite = ({ t, isDark }) => {
    const [isTermsOpen, setIsTermsOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const htmlElement = document.documentElement;
        const bodyElement = document.body;

        if (isTermsOpen) {
            htmlElement.style.overflow = 'hidden';
            htmlElement.style.height = '100%';
            bodyElement.style.overflow = 'hidden';
            bodyElement.style.height = '100%';
            bodyElement.setAttribute('data-modal-open', 'true');
        } else {
            htmlElement.style.overflow = '';
            htmlElement.style.height = '';
            bodyElement.style.overflow = '';
            bodyElement.style.height = '';
            bodyElement.removeAttribute('data-modal-open');
        }

        return () => {
            htmlElement.style.overflow = '';
            htmlElement.style.height = '';
            bodyElement.style.overflow = '';
            bodyElement.style.height = '';
            bodyElement.removeAttribute('data-modal-open');
        };
    }, [isTermsOpen]);

    // Fallback defensivo por si t.marchantite tarda en cargar
    const m = t?.marchantite || {};

    return (
        <div className={`min-h-screen pt-32 pb-24 px-6 transition-colors duration-700
      ${isDark ? 'bg-[#121212] text-[#ECECEC]' : 'bg-[#FDFDFD] text-[#1C1B1F]'}`}>

            <style dangerouslySetInnerHTML={{
                __html: `
                .no-scrollbar::-webkit-scrollbar {
                    display: none !important;
                    width: 0 !important;
                    height: 0 !important;
                }
                .no-scrollbar {
                    -ms-overflow-style: none !important;
                    scrollbar-width: none !important;
                }
                
                body[data-modal-open="true"] .nav-container {
                    z-index: 0 !important;
                    pointer-events: none !important;
                    opacity: 0 !important;
                    transition: opacity 0.3s ease;
                }
            `}} />

            <div className="container mx-auto max-w-5xl relative z-10">

                <Link to="/" className={`inline-flex items-center gap-2 mb-12 font-black uppercase text-[10px] tracking-widest transition-all hover:-translate-x-1
          ${isDark ? 'text-slate-400' : 'text-[#606C5D] hover:text-[#0096FF]'}`}>
                    <ArrowLeft size={16} /> {m.back_home || 'Volver al Inicio'}
                </Link>

                {/* HERO SECTION */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center mb-16">
                    <div className="md:col-span-7 text-left">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-2.5 h-2.5 bg-[#30D158] rounded-full animate-ping" />
                            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-[#0096FF]">
                                {m.badge}
                            </span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tighter italic mb-6">
                            {m.hero_title || 'Marchantite'}
                        </h1>
                        <p className="text-xl font-bold leading-relaxed opacity-80 mb-8">
                            {m.description}
                        </p>

                        <button
                            onClick={() => setIsTermsOpen(true)}
                            className="px-8 py-4 bg-[#0096FF]/10 border border-[#0096FF]/20 hover:bg-[#0096FF] hover:text-white text-[#0096FF] rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center gap-3 transition-all duration-300 shadow-lg shadow-[#0096FF]/5 cursor-pointer"
                        >
                            <Shield size={16} /> {m.terms_btn}
                        </button>
                    </div>

                    <div className="md:col-span-5 flex justify-center">
                        <div className={`w-64 h-64 rounded-[3rem] border backdrop-blur-3xl flex items-center justify-center relative shadow-2xl
              ${isDark ? 'bg-[#1C1B1F] border-white/10 shadow-black/50' : 'bg-white border-[#D9DCD4] shadow-[#0096FF]/10'}`}>

                            <div className="w-36 h-36 rounded-full overflow-hidden flex items-center justify-center bg-[#0096FF]/5 border border-[#0096FF]/20 p-3">
                                <img
                                    src="/logo_marchantite.png"
                                    alt="Marchantite Logo"
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentNode.innerHTML = '<span class="text-3xl font-black text-[#0096FF] italic">marchantite</span>';
                                    }}
                                />
                            </div>

                            <div className="absolute -inset-4 rounded-[3.5rem] border border-[#0096FF]/20 animate-spin" style={{ animationDuration: '18s' }} />
                        </div>
                    </div>
                </div>

                {/* PILARES DE MARCA */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 text-left">
                    <div className={`p-8 rounded-[3.5rem] border backdrop-blur-3xl
                        ${isDark ? 'bg-[#1C1B1F]/40 border-white/10' : 'bg-white border-[#D9DCD4] shadow-lg shadow-[#0096FF]/5'}`}>
                        <span className="text-[9px] font-black uppercase tracking-widest text-[#FF214B] block mb-2">{m.pillars?.local?.tag}</span>
                        <h2 className="text-xl font-black uppercase tracking-tight mb-2">{m.pillars?.local?.title}</h2>
                        <p className="text-xs font-bold opacity-60 leading-relaxed">
                            {m.pillars?.local?.desc}
                        </p>
                    </div>

                    <div className={`p-8 rounded-[3.5rem] border backdrop-blur-3xl
                        ${isDark ? 'bg-[#1C1B1F]/40 border-white/10' : 'bg-white border-[#D9DCD4] shadow-lg shadow-[#0096FF]/5'}`}>
                        <span className="text-[9px] font-black uppercase tracking-widest text-[#30D158] block mb-2">{m.pillars?.logistics?.tag}</span>
                        <h2 className="text-xl font-black uppercase tracking-tight mb-2">{m.pillars?.logistics?.title}</h2>
                        <p className="text-xs font-bold opacity-60 leading-relaxed">
                            {m.pillars?.logistics?.desc}
                        </p>
                    </div>

                    <div className={`p-8 rounded-[3.5rem] border backdrop-blur-3xl
                        ${isDark ? 'bg-[#1C1B1F]/40 border-white/10' : 'bg-white border-[#D9DCD4] shadow-lg shadow-[#0096FF]/5'}`}>
                        <span className="text-[9px] font-black uppercase tracking-widest text-[#C13BFF] block mb-2">{m.pillars?.quality?.tag}</span>
                        <h2 className="text-xl font-black uppercase tracking-tight mb-2">{m.pillars?.quality?.title}</h2>
                        <p className="text-xs font-bold opacity-60 leading-relaxed">
                            {m.pillars?.quality?.desc}
                        </p>
                    </div>
                </div>

                {/* DESGLOSE PROFUNDO EXPERIENCIA DEL CLIENTE */}
                <h3 className="text-xs font-black uppercase tracking-[0.4em] text-[#FF214B] text-left mb-6 pl-4">
                    {m.customer_experience?.section_title}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 text-left mb-16">
                    
                    {/* Tarjeta Destacada: Exploración de Mercados */}
                    <div className={`md:col-span-8 p-8 md:p-10 rounded-[3rem] border transition-all ${isDark ? 'bg-[#1C1B1F]/30 border-white/10' : 'bg-white border-[#D9DCD4] shadow-md'}`}>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-3 bg-[#FF214B]/10 rounded-2xl text-[#FF214B]">
                                <MapPin size={24} />
                            </div>
                            <div>
                                <span className="text-[9px] font-black uppercase tracking-widest text-[#FF214B] block">{m.customer_experience?.discovery?.tag}</span>
                                <h4 className="text-2xl font-black">{m.customer_experience?.discovery?.title}</h4>
                            </div>
                        </div>

                        <p className="text-xs md:text-sm font-bold opacity-75 leading-relaxed mb-6">
                            {m.customer_experience?.discovery?.desc}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs font-bold opacity-70">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#FF214B] shrink-0" />
                                <span>{m.customer_experience?.discovery?.f1}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#FF214B] shrink-0" />
                                <span>{m.customer_experience?.discovery?.f2}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#FF214B] shrink-0" />
                                <span>{m.customer_experience?.discovery?.f3}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-[#FF214B] shrink-0" />
                                <span>{m.customer_experience?.discovery?.f4}</span>
                            </div>
                        </div>
                    </div>

                    {/* Tarjeta: Programación de Entregas */}
                    <div className={`md:col-span-4 p-8 rounded-[3rem] border transition-all ${isDark ? 'bg-[#1C1B1F]/30 border-white/10' : 'bg-white border-[#D9DCD4] shadow-md'}`}>
                        <div className="p-3 bg-[#0096FF]/10 rounded-2xl text-[#0096FF] w-fit mb-4">
                            <Clock size={24} />
                        </div>
                        <h4 className="text-xl font-black mb-3">{m.customer_experience?.schedule?.title}</h4>
                        <p className="text-xs font-bold opacity-70 leading-relaxed mb-4">
                            {m.customer_experience?.schedule?.desc}
                        </p>
                        <div className="text-[10px] font-black tracking-wider uppercase text-[#0096FF] bg-[#0096FF]/10 p-3 rounded-xl">
                            {m.customer_experience?.schedule?.restriction}
                        </div>
                    </div>

                    {/* Búsqueda y Favoritos */}
                    <div className={`md:col-span-4 p-8 rounded-[3rem] border transition-all ${isDark ? 'bg-[#1C1B1F]/30 border-white/10' : 'bg-white border-[#D9DCD4] shadow-md'}`}>
                        <div className="p-3 bg-[#C13BFF]/10 rounded-2xl text-[#C13BFF] w-fit mb-4">
                            <Search size={24} />
                        </div>
                        <h4 className="text-xl font-black mb-3">{m.customer_experience?.search?.title}</h4>
                        <p className="text-xs font-bold opacity-70 leading-relaxed">
                            {m.customer_experience?.search?.desc}
                        </p>
                    </div>

                    {/* Reseñas y Notificaciones */}
                    <div className={`md:col-span-4 p-8 rounded-[3rem] border transition-all ${isDark ? 'bg-[#1C1B1F]/30 border-white/10' : 'bg-white border-[#D9DCD4] shadow-md'}`}>
                        <div className="p-3 bg-[#30D158]/10 rounded-2xl text-[#30D158] w-fit mb-4">
                            <Star size={24} />
                        </div>
                        <h4 className="text-xl font-black mb-3">{m.customer_experience?.reviews?.title}</h4>
                        <p className="text-xs font-bold opacity-70 leading-relaxed">
                            {m.customer_experience?.reviews?.desc}
                        </p>
                    </div>

                    {/* Carrito Integrado */}
                    <div className={`md:col-span-4 p-8 rounded-[3rem] border transition-all ${isDark ? 'bg-[#1C1B1F]/30 border-white/10' : 'bg-white border-[#D9DCD4] shadow-md'}`}>
                        <div className="p-3 bg-[#FF214B]/10 rounded-2xl text-[#FF214B] w-fit mb-4">
                            <ShoppingCart size={24} />
                        </div>
                        <h4 className="text-xl font-black mb-3">{m.customer_experience?.cart?.title}</h4>
                        <p className="text-xs font-bold opacity-70 leading-relaxed">
                            {m.customer_experience?.cart?.desc}
                        </p>
                    </div>

                </div>

                {/* OTROS ROLES EN LA CADENA */}
                <h3 className="text-xs font-black uppercase tracking-[0.4em] text-[#0096FF] text-left mb-6 pl-4">
                    {m.operational_support?.section_title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-16">

                    {/* LOCATARIOS */}
                    <div className={`p-8 rounded-[2.5rem] border ${isDark ? 'bg-[#1C1B1F]/20 border-white/5' : 'bg-white border-[#D9DCD4] shadow-sm'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-[#C13BFF]/10 rounded-xl text-[#C13BFF]">
                                <Store size={20} />
                            </div>
                            <h4 className="text-lg font-black text-[#C13BFF]">{m.operational_support?.merchants?.title}</h4>
                        </div>
                        <p className="text-xs font-bold opacity-60 leading-relaxed mb-4">
                            {m.operational_support?.merchants?.desc}
                        </p>
                        <ul className="space-y-2 text-xs font-bold opacity-70">
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#C13BFF]" /> {m.operational_support?.merchants?.f1}</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#C13BFF]" /> {m.operational_support?.merchants?.f2}</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#C13BFF]" /> {m.operational_support?.merchants?.f3}</li>
                        </ul>
                    </div>

                    {/* REPARTIDORES */}
                    <div className={`p-8 rounded-[2.5rem] border ${isDark ? 'bg-[#1C1B1F]/20 border-white/5' : 'bg-white border-[#D9DCD4] shadow-sm'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-[#30D158]/10 rounded-xl text-[#30D158]">
                                <Truck size={20} />
                            </div>
                            <h4 className="text-lg font-black text-[#30D158]">{m.operational_support?.couriers?.title}</h4>
                        </div>
                        <p className="text-xs font-bold opacity-60 leading-relaxed mb-4">
                            {m.operational_support?.couriers?.desc}
                        </p>
                        <ul className="space-y-2 text-xs font-bold opacity-70">
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#30D158]" /> {m.operational_support?.couriers?.f1}</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#30D158]" /> {m.operational_support?.couriers?.f2}</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#30D158]" /> {m.operational_support?.couriers?.f3}</li>
                        </ul>
                    </div>

                </div>

                {/* TABLA PÚBLICA DE TARIFAS DE ENVÍO */}
                <div className={`p-8 md:p-12 rounded-[3.5rem] border backdrop-blur-3xl mb-12 text-left
                    ${isDark ? 'bg-[#1C1B1F]/40 border-white/10' : 'bg-white border-[#D9DCD4] shadow-lg shadow-[#0096FF]/5'}`}>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-[#0096FF]/10 rounded-lg text-[#0096FF]">
                            <Navigation size={22} />
                        </div>
                        <div>
                            <span className="text-[9px] font-black uppercase tracking-widest text-[#0096FF] block">{m.shipping_rates?.tag}</span>
                            <h2 className="text-2xl font-black uppercase tracking-tight">{m.shipping_rates?.title}</h2>
                        </div>
                    </div>

                    <p className="text-xs font-bold opacity-70 leading-relaxed mb-6">
                        {m.shipping_rates?.desc}
                    </p>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs font-bold border-collapse">
                            <thead>
                                <tr className={`border-b ${isDark ? 'border-white/10 text-slate-400' : 'border-[#D9DCD4] text-[#606C5D]'}`}>
                                    <th className="py-3 px-4">{m.shipping_rates?.col_range}</th>
                                    <th className="py-3 px-4">{m.shipping_rates?.col_cost}</th>
                                    <th className="py-3 px-4">{m.shipping_rates?.col_conditions}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#D9DCD4]/20">
                                <tr>
                                    <td className="py-3 px-4">{m.shipping_rates?.range1}</td>
                                    <td className="py-3 px-4 text-[#30D158] font-black">$15 MXN</td>
                                    <td className="py-3 px-4 opacity-60" rowSpan={4}>
                                        • {m.shipping_rates?.conditions_detail?.coverage}<br />
                                        • {m.shipping_rates?.conditions_detail?.capacity}<br />
                                        • {m.shipping_rates?.conditions_detail?.guarantee}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="py-3 px-4">{m.shipping_rates?.range2}</td>
                                    <td className="py-3 px-4 text-[#30D158] font-black">$20 MXN</td>
                                </tr>
                                <tr>
                                    <td className="py-3 px-4">{m.shipping_rates?.range3}</td>
                                    <td className="py-3 px-4 text-[#30D158] font-black">$25 MXN</td>
                                </tr>
                                <tr>
                                    <td className="py-3 px-4">{m.shipping_rates?.range4}</td>
                                    <td className="py-3 px-4 text-[#30D158] font-black">$35 MXN</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>

            {/* MODAL DE POLÍTICAS DE SERVICIO */}
            <AnimatePresence>
                {isTermsOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 md:p-6 bg-[#121212]/95 backdrop-blur-3xl"
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            className={`relative w-full max-w-2xl p-6 sm:p-8 md:p-12 rounded-[2.5rem] md:rounded-[3rem] border shadow-2xl text-left flex flex-col max-h-[80vh]
                ${isDark ? 'bg-[#1C1B1F] border-white/10 text-[#ECECEC]' : 'bg-white border-[#D9DCD4] text-[#1C1B1F]'}`}
                        >
                            <button
                                onClick={() => setIsTermsOpen(false)}
                                className={`absolute top-4 right-4 md:top-6 md:right-6 p-2 rounded-full border transition-colors z-[100]
                  ${isDark ? 'border-white/10 hover:bg-white/10 bg-[#1C1B1F]' : 'border-[#D9DCD4] hover:bg-slate-100 bg-white'}`}
                            >
                                <X size={18} />
                            </button>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 pr-10">
                                <div className="w-12 h-12 rounded-2xl overflow-hidden border border-[#0096FF]/20 flex items-center justify-center bg-[#0096FF]/10 shrink-0 p-2">
                                    <Shield className="text-[#0096FF] w-full h-full" />
                                </div>
                                <div className="space-y-1">
                                    <h2 className="text-xl md:text-2xl font-black tracking-tight text-[#0096FF] leading-tight">
                                        {m.policy_modal?.title}
                                    </h2>
                                    <p className="text-[8px] md:text-[9px] font-black uppercase tracking-wider opacity-40">
                                        {m.policy_modal?.subtitle}
                                    </p>
                                </div>
                            </div>

                            <div className="h-px bg-current opacity-10 mb-6 shrink-0" />
                            <div className="space-y-6 text-xs md:text-sm font-bold opacity-70 leading-relaxed overflow-y-auto pr-2 flex-grow no-scrollbar">
                                <h3 className="text-sm md:text-base font-black opacity-100 text-[#0096FF]">
                                    {m.policy_modal?.section1_title}
                                </h3>
                                <p>{m.policy_modal?.section1_text}</p>

                                <h3 className="text-sm md:text-base font-black opacity-100 text-[#0096FF]">
                                    {m.policy_modal?.section2_title}
                                </h3>
                                <p>{m.policy_modal?.section2_text}</p>

                                <h3 className="text-sm md:text-base font-black opacity-100 text-[#0096FF]">
                                    {m.policy_modal?.section3_title}
                                </h3>
                                <p>{m.policy_modal?.section3_text}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default Marchantite;